import { useCallback, useEffect, useRef, useContext } from "react";
import { useServiceCenter } from "../contexts";
import { ServiceCenterAPI } from "../services";
import {
  formatApiMessages,
  createOptimisticMessage,
  createOptimisticAudioMessage,
  createOptimisticMediaMessage,
  getMediaTypeFromFile,
} from "../utils/messageProcessor";
import { PAGINATION } from "../utils/constants";
import UserContext from "../../../contexts/UserContext";

/**
 * Hook para gerenciar mensagens do chat
 */
const useMessages = () => {
  const {
    selectedAccount,
    accounts,
    selectedConversation,
    messages,
    messagesOffset,
    hasMoreMessages,
    loading,
    setMessages,
    addOlderMessages,
    updateMessage,
    setLoading,
  } = useServiceCenter();

  /**
   * Resolve qual account usar como `session` para buscar/enviar mensagens
   * desta conversa. Em modo omnichannel, selectedAccount pode ser de outro
   * canal — precisa achar a account correspondente ao canal da conversa.
   *
   * Constraint do produto: máx 1 account por canal, então a busca é direta.
   */
  const accountForConversation = useCallback(() => {
    const channel = selectedConversation?.channel;
    if (!channel || !Array.isArray(accounts)) {
      return selectedAccount;
    }
    const match = accounts.find((a) => a.channel === channel);
    return match || selectedAccount;
  }, [selectedConversation?.channel, accounts, selectedAccount]);

  const { user } = useContext(UserContext);
  const scrollRef = useRef(null);
  const isLoadingRef = useRef(false);
  // Guard reentrante: bloqueia chamadas concorrentes de send* enquanto
  // uma operação de envio (texto/áudio/arquivo) está pendente. Defesa
  // em profundidade contra clique-clique mesmo quando o ChatInput não
  // bloqueia (ex: shortcut, reentrada via WS, etc.).
  const isSendingRef = useRef(false);

  /**
   * Gera a assinatura do operador para mensagens
   * Prioriza operator_name do usuário logado (editável em tempo real),
   * com fallback para current_attendant_name da conversa.
   * Formato: *Nome - Departamento*
   */
  const getOperatorSignature = useCallback(() => {
    // Só incluir assinatura se estiver em atendimento
    if (!selectedConversation || selectedConversation.service_status !== "in_service") {
      return null;
    }

    // Priorizar operator_name do usuário logado (atualizado em tempo real)
    const name = user?.operator_name
      || selectedConversation.current_attendant_name;
    const department = selectedConversation.current_department_name;

    if (!name) return null;

    if (department) {
      return `*${name} - ${department}*`;
    }
    return `*${name}*`;
  }, [selectedConversation, user?.operator_name]);

  /**
   * Scroll para o final das mensagens
   */
  const scrollToBottom = useCallback((smooth = true) => {
    if (scrollRef.current) {
      const scrollContainer = scrollRef.current._container || scrollRef.current;
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  }, []);

  /**
   * Carrega mensagens iniciais da conversa
   */
  const loadMessages = useCallback(async () => {
    const acct = accountForConversation();
    if (!acct?.phone || !selectedConversation?.phone) return;
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading({ messages: true });

    try {
      const response = await ServiceCenterAPI.getMessages(
        acct.phone,
        selectedConversation.phone,
        0,
        PAGINATION.MESSAGES_PER_PAGE,
        selectedConversation.channel || acct.channel || 'whatsapp'
      );

      const rawMessages = response.messages || response.results || [];
      const hasMore = response.has_more !== undefined ? response.has_more : false;

      // Reverter ordem: API retorna DESC, queremos ASC
      const messagesInOrder = [...rawMessages].reverse();

      // Formatar mensagens
      const formatted = formatApiMessages(
        messagesInOrder,
        selectedConversation.name
      );

      // ChatMessages já agrupa por data com groupMessagesByDate
      setMessages(formatted, rawMessages.length, hasMore);

      // Scroll para o final após carregar
      setTimeout(() => scrollToBottom(false), 100);
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
    } finally {
      setLoading({ messages: false });
      isLoadingRef.current = false;
    }
  }, [
    accountForConversation,
    selectedConversation?.phone,
    selectedConversation?.name,
    selectedConversation?.channel,
    setMessages,
    setLoading,
    scrollToBottom,
  ]);

  /**
   * Carrega mensagens mais antigas (paginação)
   */
  const loadMoreMessages = useCallback(async () => {
    const acctCheck = accountForConversation();
    if (
      !acctCheck?.phone ||
      !selectedConversation?.phone ||
      !hasMoreMessages ||
      loading.messages
    ) {
      return;
    }

    setLoading({ messages: true });

    try {
      const acct = accountForConversation();
      const response = await ServiceCenterAPI.getMessages(
        acct?.phone,
        selectedConversation.phone,
        messagesOffset,
        PAGINATION.MESSAGES_PER_PAGE,
        selectedConversation.channel || acct?.channel || 'whatsapp'
      );

      const rawMessages = response.messages || response.results || [];
      const hasMore = response.has_more !== undefined ? response.has_more : false;

      if (rawMessages.length > 0) {
        // Reverter ordem
        const messagesInOrder = [...rawMessages].reverse();

        // Formatar mensagens
        const formatted = formatApiMessages(
          messagesInOrder,
          selectedConversation.name
        );

        // ChatMessages já agrupa por data com groupMessagesByDate
        addOlderMessages(
          formatted,
          messagesOffset + rawMessages.length,
          hasMore
        );
      } else {
        addOlderMessages([], messagesOffset, false);
      }
    } catch (error) {
      console.error("Erro ao carregar mais mensagens:", error);
    } finally {
      setLoading({ messages: false });
    }
  }, [
    accountForConversation,
    selectedConversation?.phone,
    selectedConversation?.name,
    selectedConversation?.channel,
    hasMoreMessages,
    loading.messages,
    messagesOffset,
    addOlderMessages,
    setLoading,
  ]);

  /**
   * Envia mensagem de texto.
   *
   * @param {string} text - Texto da mensagem.
   * @param {object} options - Opcional: `{ replyContext }` quando o operador
   *   está respondendo a um comentário Instagram. Estrutura esperada:
   *   `{ commentId, mediaPostId, snippet, senderName }`.
   */
  const sendMessage = useCallback(
    async (text, options = {}) => {
      if (!text?.trim() || !selectedAccount || !selectedConversation) {
        return false;
      }
      const { replyContext } = options;
      if (isSendingRef.current) return false;
      isSendingRef.current = true;

      // Obter assinatura do operador
      const signature = getOperatorSignature();

      // Criar mensagem otimista (com assinatura para exibição na UI)
      const optimisticMsg = createOptimisticMessage(text);
      if (signature) {
        optimisticMsg.operatorSignature = signature;
      }

      // Adicionar mensagem otimista
      const messagesWithNew = [...messages, optimisticMsg];

      // Atualizar state imediatamente
      setMessages(messagesWithNew, messagesOffset, hasMoreMessages);

      // Scroll para o final
      setTimeout(() => scrollToBottom(), 100);

      try {
        setLoading({ sending: true });

        // Mensagem para a API: incluir assinatura se existir
        const messageToSend = signature ? `${signature}\n${text}` : text;

        // Multi-canal: usa account do MESMO canal da conversa (omnichannel).
        // Crítico — em modo "Todos" o selectedAccount pode ser de outro canal.
        const channel = selectedConversation.channel || 'whatsapp';
        const acct = accountForConversation();

        let payload;
        if (channel === 'instagram') {
          payload = {
            channel: 'instagram',
            session: acct?.ig_business_account_id || acct?.phone,
            user_id: selectedConversation.channel_user_id || selectedConversation.phone,
            message: messageToSend,
            message_kind: replyContext?.commentId ? 'comment' : 'dm',
            comment_id: replyContext?.commentId || undefined,
          };
        } else if (channel === 'messenger') {
          payload = {
            channel: 'messenger',
            session: acct?.facebook_page_id || acct?.phone,
            user_id:
              selectedConversation.channel_user_id
              || selectedConversation.messenger_psid
              || selectedConversation.phone,
            message: messageToSend,
          };
        } else {
          payload = {
            session: acct?.phone,
            unique: acct?.unique,
            token: acct?.token,
            message: messageToSend,
            phone:
              selectedConversation.phone?.replace(/[^\d+]/g, "") ||
              selectedConversation.phone,
          };
        }

        await ServiceCenterAPI.sendMessage(payload);

        // Atualizar status da mensagem para delivered
        updateMessage(optimisticMsg.id, { status: "delivered" });

        return true;
      } catch (error) {
        console.error("Erro ao enviar mensagem:", error);

        // Erro 422 = janela 24h IG expirada — mensagem amigável
        const errorMsg = error.response?.status === 422
          ? (error.response?.data?.error || 'Janela de 24h expirou')
          : 'Falha ao enviar mensagem';

        // Marcar mensagem como erro
        updateMessage(optimisticMsg.id, {
          status: "error",
          errorMessage: errorMsg,
        });

        return false;
      } finally {
        setLoading({ sending: false });
        isSendingRef.current = false;
      }
    },
    [
      selectedAccount,
      selectedConversation,
      messages,
      messagesOffset,
      hasMoreMessages,
      setMessages,
      updateMessage,
      setLoading,
      scrollToBottom,
      getOperatorSignature,
    ]
  );

  /**
   * Envia mensagem de áudio
   */
  const sendAudioMessage = useCallback(
    async (audioBlob, audioUrl, duration = 0) => {
      if (!audioBlob || !selectedAccount || !selectedConversation) {
        return false;
      }
      if (isSendingRef.current) return false;
      isSendingRef.current = true;

      // Criar mensagem otimista
      const optimisticMsg = createOptimisticAudioMessage(audioUrl, duration);

      // Adicionar mensagem otimista
      const messagesWithNew = [...messages, optimisticMsg];

      setMessages(messagesWithNew, messagesOffset, hasMoreMessages);

      setTimeout(() => scrollToBottom(), 100);

      try {
        setLoading({ sending: true });

        const normalizedPhone = selectedConversation.phone?.replace(/[^\d+]/g, "") || selectedConversation.phone;
        const formData = new FormData();
        formData.append("session", selectedAccount.phone);
        formData.append("phone", normalizedPhone);
        formData.append("message", "🎤 Mensagem de áudio");
        formData.append("type", "media");
        formData.append("media_type", "audio");
        formData.append("media_file", audioBlob, "audio.ogg");
        formData.append("priority", "1");
        formData.append("token", selectedAccount.token);
        formData.append("unique", selectedAccount.unique);
        formData.append("recipient", normalizedPhone);

        await ServiceCenterAPI.sendAudioMessage(formData);

        updateMessage(optimisticMsg.id, { status: "delivered" });

        return true;
      } catch (error) {
        console.error("Erro ao enviar áudio:", error);

        updateMessage(optimisticMsg.id, {
          status: "error",
          errorMessage: "Falha ao enviar áudio",
        });

        return false;
      } finally {
        setLoading({ sending: false });
        isSendingRef.current = false;
      }
    },
    [
      selectedAccount,
      selectedConversation,
      messages,
      messagesOffset,
      hasMoreMessages,
      setMessages,
      updateMessage,
      setLoading,
      scrollToBottom,
    ]
  );

  /**
   * Envia mensagem de arquivo (imagem, vídeo, documento)
   */
  const sendFileMessage = useCallback(
    async (file, caption = "") => {
      if (!file || !selectedAccount || !selectedConversation) {
        return false;
      }
      if (isSendingRef.current) return false;
      isSendingRef.current = true;

      // Criar URL de preview local para imagens
      let previewUrl = null;
      const mediaType = getMediaTypeFromFile(file);
      if (mediaType === "image" || mediaType === "video") {
        previewUrl = URL.createObjectURL(file);
      }

      // Criar mensagem otimista
      const optimisticMsg = createOptimisticMediaMessage(file, caption, previewUrl);

      // Adicionar mensagem otimista
      const messagesWithNew = [...messages, optimisticMsg];
      setMessages(messagesWithNew, messagesOffset, hasMoreMessages);

      setTimeout(() => scrollToBottom(), 100);

      try {
        setLoading({ sending: true });

        const normalizedPhone = selectedConversation.phone?.replace(/[^\d+]/g, "") || selectedConversation.phone;
        const formData = new FormData();
        formData.append("session", selectedAccount.phone);
        formData.append("phone", normalizedPhone);
        formData.append("message", caption || "");
        formData.append("type", "media");
        formData.append("media_type", mediaType);
        formData.append("media_file", file, file.name);
        formData.append("priority", "1");
        formData.append("token", selectedAccount.token);
        formData.append("unique", selectedAccount.unique);
        formData.append("recipient", normalizedPhone);

        await ServiceCenterAPI.sendMediaMessage(formData);

        updateMessage(optimisticMsg.id, { status: "delivered" });

        return true;
      } catch (error) {
        console.error("Erro ao enviar arquivo:", error);

        updateMessage(optimisticMsg.id, {
          status: "error",
          errorMessage: "Falha ao enviar arquivo",
        });

        return false;
      } finally {
        setLoading({ sending: false });
        isSendingRef.current = false;
        // Limpar URL de preview
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
      }
    },
    [
      selectedAccount,
      selectedConversation,
      messages,
      messagesOffset,
      hasMoreMessages,
      setMessages,
      updateMessage,
      setLoading,
      scrollToBottom,
    ]
  );

  /**
   * Adiciona mensagem recebida via WebSocket.
   *
   * Correlaciona otimísticas pelo conteúdo + janela de timestamp pra
   * evitar a corrida onde 2 envios em sequência (A, B) viram 2× B
   * quando o WS entrega B antes de A. Estratégia:
   *
   *   1. Match exato de texto + timestamp dentro de 60s → substitui.
   *   2. Match de mídia: optimistic tem `has_media` e msg também →
   *      substitui o primeiro optimistic de mídia (mídia não tem texto
   *      confiável pra comparar — caption pode vir vazia).
   *   3. Sem match → enfileira como nova msg.
   *
   * Se não houver optimistic compatível, é uma echo cross-device
   * (ex: usuário enviou pelo celular) — adiciona normalmente.
   */
  const handleIncomingMessage = useCallback(
    (processedMessages) => {
      if (!processedMessages || processedMessages.length === 0) return;

      const TIMESTAMP_WINDOW_MS = 60_000;
      let updatedMessages = [...messages];

      for (const msg of processedMessages) {
        if (!msg.isMe) {
          updatedMessages.push(msg);
          continue;
        }

        const incomingText = (msg.text || "").trim();
        const incomingTs = msg.timestamp || Date.now();

        let matchIdx = -1;

        if (msg.has_media) {
          // Mídia: caption pode vir diferente entre optimistic e real.
          // Casa pelo primeiro optimistic de mídia.
          matchIdx = updatedMessages.findIndex(
            (m) => m.isOptimistic && m.isMe && m.has_media
          );
        } else {
          // Texto: match exato de conteúdo + janela de tempo.
          matchIdx = updatedMessages.findIndex((m) => {
            if (!m.isOptimistic || !m.isMe || m.has_media) return false;
            const optText = (m.text || "").trim();
            const optTs = m.timestamp || 0;
            return (
              optText === incomingText &&
              Math.abs(incomingTs - optTs) <= TIMESTAMP_WINDOW_MS
            );
          });

          // Fallback: se não casou por texto exato, casa pelo primeiro
          // optimistic de texto (mantém compat com mensagens onde a
          // assinatura do operador foi prefixada e o texto não bate).
          if (matchIdx === -1) {
            matchIdx = updatedMessages.findIndex(
              (m) =>
                m.isOptimistic && m.isMe && !m.has_media &&
                (m.text || "").trim() &&
                incomingText.includes((m.text || "").trim())
            );
          }
        }

        if (matchIdx !== -1) {
          updatedMessages[matchIdx] = { ...msg, status: "delivered" };
        } else {
          updatedMessages.push(msg);
        }
      }

      setMessages(updatedMessages, messagesOffset, hasMoreMessages);

      // Scroll para o final
      setTimeout(() => scrollToBottom(), 100);
    },
    [messages, messagesOffset, hasMoreMessages, setMessages, scrollToBottom]
  );

  /**
   * Atualiza status de mensagem
   */
  const handleMessageStatusUpdate = useCallback(
    (messageId, status, errorMessage = null) => {
      updateMessage(messageId, { status, errorMessage });
    },
    [updateMessage]
  );

  // Refs estáveis pras funções que mudam a cada render (porque dependem
  // de `messages`/offsets). O useEffect de troca de conversa só quer
  // saber quando `selectedConversation.id` muda — usar a ref evita
  // o lint warning de exhaustive-deps E o reload acidental quando
  // qualquer dep interna de loadMessages mudar.
  const loadMessagesRef = useRef(loadMessages);
  const scrollToBottomRef = useRef(scrollToBottom);
  useEffect(() => {
    loadMessagesRef.current = loadMessages;
    scrollToBottomRef.current = scrollToBottom;
  }, [loadMessages, scrollToBottom]);

  // Carregar mensagens quando conversa mudar.
  // Dispara apenas em mudança de conversa (id). As funções vêm de
  // ref pra não disparar reload em re-renders por outras causas.
  useEffect(() => {
    if (!selectedConversation?.phone) return;
    if (messages.length === 0) {
      loadMessagesRef.current();
    } else {
      // Já tem mensagens (cache local) — só ajusta scroll.
      setTimeout(() => scrollToBottomRef.current(false), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.id]);

  return {
    // State
    messages,
    hasMoreMessages,
    isLoading: loading.messages,
    isSending: loading.sending,

    // Refs
    scrollRef,

    // Actions
    loadMessages,
    loadMoreMessages,
    sendMessage,
    sendAudioMessage,
    sendFileMessage,
    handleIncomingMessage,
    handleMessageStatusUpdate,
    scrollToBottom,
  };
};

export default useMessages;
